import {sleep} from '@benchling/js-utils';
import moment from 'moment';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import DocumentStore from 'notebook/documents/DocumentStore';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import {TEMPLATE_DAY_0} from 'notebook/entry-templates/TemplateConstants';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import APIUtil from 'util/APIUtil';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';

/* Utility functions that are agnostic between Entry vs StageEntry vs Template */

const DATE_FORMAT = 'YYYY-MM-DD';

export async function waitForEntryOrTemplateSaved(
  entryOrTemplateId: string,
  intervalMilliseconds: number,
  totalWaitSeconds: number
): Promise<boolean> {
  for (let i = 0; ; i++) {
    let isSavingCompleted;
    const entryType = APIUtil.apiIdToType(entryOrTemplateId);
    if (entryType === ModelTypeConstants.STAGE_ENTRY) {
      isSavingCompleted =
        StageEntryStore.inStore(entryOrTemplateId) && !StageEntryStore.hasUnsavedChanges(entryOrTemplateId);
    } else if (entryType === ModelTypeConstants.ENTRY) {
      isSavingCompleted =
        EntryStore.inStore(entryOrTemplateId) && !EntryStore.hasUnsavedChanges(entryOrTemplateId);
    } else if (entryType === ModelTypeConstants.TEMPLATE) {
      isSavingCompleted =
        TemplateStore.inStore(entryOrTemplateId) && !TemplateStore.hasUnsavedChanges(entryOrTemplateId);
    } else if (entryType === ModelTypeConstants.SUBTEMPLATE) {
      isSavingCompleted =
        SubtemplateStore.inStore(entryOrTemplateId) && !SubtemplateStore.hasUnsavedChanges(entryOrTemplateId);
    } else if (entryType === ModelTypeConstants.DOCUMENT) {
      isSavingCompleted =
        DocumentStore.inStore(entryOrTemplateId) && !DocumentStore.hasUnsavedChanges(entryOrTemplateId);
    } else {
      assert(false, 'Unexpected entry or template type', {entryOrTemplateId});
    }

    if (isSavingCompleted) {
      return true;
    } else if (i >= (totalWaitSeconds * 1000) / intervalMilliseconds) {
      return false;
    } else {
      await sleep(intervalMilliseconds);
    }
  }
}

export function getDefaultDateForInsertEntryDay(
  date: string,
  previousDate: string,
  nextDate: string,
  isTemplate: boolean
): string {
  if (date) {
    return date;
  }
  if (!previousDate && !nextDate) {
    return !isTemplate ? moment().format(DATE_FORMAT) : TEMPLATE_DAY_0;
  }
  if (previousDate === nextDate) {
    return moment(previousDate).format(DATE_FORMAT);
  }
  if (previousDate) {
    return moment(previousDate).add(1, 'day').format(DATE_FORMAT);
  }
  return moment(nextDate).subtract(1, 'day').format(DATE_FORMAT);
}

export function isInStore(documentLikeId: string): boolean {
  const entryType = APIUtil.apiIdToType(documentLikeId);
  if (entryType === ModelTypeConstants.STAGE_ENTRY) {
    return StageEntryStore.inStore(documentLikeId);
  } else if (entryType === ModelTypeConstants.ENTRY) {
    return EntryStore.inStore(documentLikeId);
  } else if (entryType === ModelTypeConstants.TEMPLATE) {
    return TemplateStore.inStore(documentLikeId);
  } else if (entryType === ModelTypeConstants.SUBTEMPLATE) {
    return SubtemplateStore.inStore(documentLikeId);
  } else if (entryType === ModelTypeConstants.DOCUMENT) {
    return DocumentStore.inStore(documentLikeId);
  } else {
    return false;
  }
}

export function hasUnsavedChanges(documentLikeId: string): boolean {
  const entryType = APIUtil.apiIdToType(documentLikeId);
  if (entryType === ModelTypeConstants.STAGE_ENTRY) {
    return StageEntryStore.hasUnsavedChanges(documentLikeId);
  } else if (entryType === ModelTypeConstants.ENTRY) {
    return EntryStore.hasUnsavedChanges(documentLikeId);
  } else if (entryType === ModelTypeConstants.TEMPLATE) {
    return TemplateStore.hasUnsavedChanges(documentLikeId);
  } else if (entryType === ModelTypeConstants.SUBTEMPLATE) {
    return SubtemplateStore.hasUnsavedChanges(documentLikeId);
  } else if (entryType === ModelTypeConstants.DOCUMENT) {
    return DocumentStore.hasUnsavedChanges(documentLikeId);
  } else {
    return false;
  }
}
